import { useEffect, useMemo, useState } from "react";
import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { DataTablePagination } from "@/components/ui/table-pagination";

import { DEFAULT_PAGE_SIZE } from "./components/constants";
import { createColumns } from "./components/columns";
import { ErrorMessages } from "./components/ErrorMessages";
import { TradersDataTable } from "./components/TradersDataTable";
import { type ListProps } from "./components/types";
import { filterTraders, getTraderRowId } from "./components/utils";

export { TRADER_SORT_OPTIONS } from "./components/constants";
export type { TraderListItem } from "./components/types";

export const List = ({
  isFetching = false,
  listTraderRepresentativesErrorMessage = "",
  traderOrganizationActivityErrorMessage = "",
  onClickView,
  onSortingChange,
  pageSize = DEFAULT_PAGE_SIZE,
  searchTerm = "",
  sorting,
  traders = [],
}: ListProps) => {
  const { t } = useTranslation();
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSize > 0 ? pageSize : DEFAULT_PAGE_SIZE,
  });
  const resolvedSorting = sorting ?? internalSorting;
  const handleSortingChange = onSortingChange ?? setInternalSorting;
  const errorMessages = [
    traderOrganizationActivityErrorMessage,
    listTraderRepresentativesErrorMessage,
  ].filter((message): message is string => Boolean(message));
  const filteredTraders = useMemo(() => filterTraders(traders, searchTerm), [searchTerm, traders]);
  const columns = useMemo(
    () =>
      createColumns({
        onClickView,
        t,
      }),
    [onClickView, t]
  );

  useEffect(() => {
    setPagination(currentPagination =>
      currentPagination.pageIndex === 0 ? currentPagination : { ...currentPagination, pageIndex: 0 }
    );
  }, [resolvedSorting, searchTerm]);

  useEffect(() => {
    setPagination(currentPagination => {
      const pageCount = Math.max(1, Math.ceil(filteredTraders.length / currentPagination.pageSize));
      const nextPageIndex = Math.min(currentPagination.pageIndex, pageCount - 1);

      return nextPageIndex === currentPagination.pageIndex
        ? currentPagination
        : { ...currentPagination, pageIndex: nextPageIndex };
    });
  }, [filteredTraders.length]);

  const table = useReactTable({
    columns,
    data: filteredTraders,
    enableMultiSort: false,
    enableSortingRemoval: false,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: getTraderRowId,
    getSortedRowModel: getSortedRowModel(),
    onPaginationChange: setPagination,
    onSortingChange: handleSortingChange,
    state: {
      pagination,
      sorting: resolvedSorting,
    },
  });

  if (errorMessages.length > 0) {
    return <ErrorMessages messages={errorMessages} />;
  }

  return (
    <div className="overflow-hidden rounded-md border border-border bg-background">
      <TradersDataTable emptyMessage={t("traders.empty")} isLoading={isFetching} table={table} />
      {filteredTraders.length > 0 ? <DataTablePagination table={table} /> : null}
    </div>
  );
};

export default List;
